import React, { useState, useEffect, useRef, useCallback } from "react";
import { findDOMNode } from "react-dom";
import PropTypes from "prop-types";
import { Trans, t } from "@lingui/macro";
import { useHistory } from "react-router-dom";

import { i18n } from "base/i18n";
import { InfiniteScrollList } from "widgets/infinitescroll";
import { SCROLLBAR_THICKNESS } from "widgets/scrollbar";
import { EmptyState, EMPTY_STATE_SIZES } from "widgets/layouts";
import { Icon } from "widgets/datadisplay";
import { MouseWheelListen } from "base/utils";

import { Postbox } from "../components/postbox";
import { Post } from "../components/post";
import { PostSkeleton } from "../components/skeleton_loader/index";
import { useChannelStore } from "@/common/store/channel.store";
import { ChannelService } from "@/common/service/channel.service";
import { useCommentStore } from "../common/store/comment.store";
import {
  ANNOUNCEMENTS_CHANNEL_ID,
  CHANNEL_EVENTS,
  CHANNEL_CONST,
  PAGE_SIZE
} from "../common/utils/constant";
import { usePostStore } from "../common/store/post.store";
import { PostService } from "@/common/service/post.service";
import { FloatingButton } from "@/components/floatingbutton";

import styles from "./feed.css";

import EmptyFeedIcon from "../common/icons/EmptyFeed.svg";
import UpArrowIcon from "../common/icons/UpArrow.svg";

Feed.propTypes = {
  teamId: PropTypes.string,
  mediumId: PropTypes.string,
  unreadChilds: PropTypes.object,
  roles: PropTypes.array
};

export function Feed({ teamId, mediumId, unreadChilds, roles }) {
  const history = useHistory();
  const postNotification = history.location.state;
  const pageNumber = useRef(2);
  const endList = useRef(true);
  const currentPage = useRef(null);
  const postListRef = useRef(null);
  const scrollRef = useRef(null);
  const skeletonDelay = useRef(true);
  const notifcationPinned = useRef(0);

  const { updateChild } = useChannelStore.getState();
  const postList = usePostStore(
    // eslint-disable-next-line kf/hook-func-name
    useCallback((state) => [...(state.postList[mediumId] || [])], [mediumId])
  );
  let { pinnedPostIds, newPostIds, newPosts } = usePostStore((state) => {
    return {
      pinnedPostIds: state.pinnedPostIds,
      newPostIds: state.newPostIds,
      newPosts: state.newPosts
    };
  });

  const [loading, setLoading] = useState(true);
  const [newPostLoading, setNewPostLoading] = useState(false);
  const [getPinnedData, setGetPinnedData] = useState(false);
  const [enableEditor, setEnableEditor] = useState(false);
  const [nonRenderPosts, setNonRenderPosts] = useState([]);
  const [showFloatingArrow, setShowFloatingArrow] = useState(false);

  useEffect(function elementListener() {
    let overlayElem = document.getElementById("postOverlay");
    let parentElement = document.querySelector(
      `.${styles.postListWrapper} .os-viewport-native-scrollbars-invisible`
    );
    let { listen, unListen } = MouseWheelListen(
      overlayElem,
      (dx, dy, dz, evt) => {
        parentElement.scrollTop = parentElement.scrollTop + dy;
      }
    );
    listen();
    return () => {
      unListen();
      if (
        (newPostIds[mediumId] && newPostIds[mediumId].length) ||
        (newPosts[mediumId] && newPosts[mediumId].length)
      ) {
        syncNewPosts();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    function getPostList() {
      setLoading(true);
      if (postList && postList.length > 0) {
        setLoading(false);
        endList.current = postList.length % PAGE_SIZE === 0;
        pageNumber.current = Math.ceil(postList.length / PAGE_SIZE) + 1;
        return;
      }
      if (!teamId || !mediumId) return;
      let getPostList = PostService.getPostList(teamId, mediumId, 1, 10);
      getPostList
        .onResponse((posts) => {
          setLoading(false);
          skeletonDelay.current = false;
          endList.current = posts.length === PAGE_SIZE;
          usePostStore.getState().setPostList(posts, mediumId);
          if (unreadChilds[mediumId]?.UnreadPosts) {
            let [readupdate] = ChannelService.channelReadService(
              teamId,
              mediumId
            );
            readupdate();
          }
          const { getCommentsBatch } = useCommentStore.getState();
          let postIds = posts.map((item) => item._id);
          if (
            postNotification?.notificationData &&
            !postNotification.notificationData[0].Properties.CommentId
          ) {
            const item = postNotification.notificationData[0];
            if (!postIds.includes(item.EntityId)) {
              postIds.push(item.EntityId);
            }
          }
          if (pinnedPostIds?.length > 0) {
            postIds = postIds.concat(pinnedPostIds);
          }
          getCommentsBatch({
            medium: CHANNEL_CONST.CHANNEL,
            mediumId: mediumId,
            teamId: teamId,
            postIds: postIds
          });
        })
        .onError((err) => {
          console.info("get post error", err);
        });
      return () => {
        setLoading(true);
        getPostList.cancel();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mediumId]
  );

  useEffect(
    function getNotificationData() {
      let notificationList = [];
      postNotification?.notificationData?.map((notification) => {
        return notificationList.push(notification.EntityId);
      });
      setNonRenderPosts(notificationList);
    },
    [postNotification]
  );

  useEffect(
    function getPinnedPostData() {
      if (pinnedPostIds.length > 0) {
        if (postNotification) {
          postNotification?.notificationData?.forEach((notification) => {
            if (pinnedPostIds.includes(notification.EntityId)) {
              notifcationPinned.current += 1;
            }
          });
        }
        if (usePostStore.getState().checkPostData(pinnedPostIds)) {
          setGetPinnedData(true);
          return;
        }
        let [getPostBatchResponse] = usePostStore
          .getState()
          .getPostByIdBatch(teamId, mediumId, pinnedPostIds);
        getPostBatchResponse
          .then(() => {
            setGetPinnedData(true);
          })
          .catch((err) => {
            console.info(err);
          });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mediumId, pinnedPostIds]
  );

  useEffect(
    function markReadHandler() {
      if (
        unreadChilds[mediumId]?.UnreadPosts ||
        (newPostIds?.mediumId && newPostIds?.mediumId.length === 0) ||
        (newPosts?.mediumId && newPosts?.mediumId?.length === 0)
      ) {
        let [readupdate] = ChannelService.channelReadService(teamId, mediumId);
        readupdate();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unreadChilds]
  );

  function syncNewPosts() {
    if (newPostIds[mediumId] && newPostIds[mediumId].length > 0)
      setNewPostLoading(true);
    const { syncNewPosts } = usePostStore.getState();
    const [syncNewPostsResponse] = syncNewPosts(teamId, mediumId);
    syncNewPostsResponse.then(() => {
      setNewPostLoading(false);
      if (unreadChilds[mediumId]?.UnreadPosts) {
        let [readupdate] = ChannelService.channelReadService(teamId, mediumId);
        readupdate();
      }
      let postboxElement = findDOMNode(postListRef.current);
      postboxElement.scrollIntoView(true);
    });
  }

  function onEndReached() {
    if (currentPage.current === pageNumber.current) return;
    currentPage.current = pageNumber.current;
    if (endList.current) {
      setLoading(true);
      const { getMorePosts } = usePostStore.getState();
      let [getPostsResult] = getMorePosts(
        teamId,
        mediumId,
        pageNumber.current,
        PAGE_SIZE
      );
      getPostsResult
        .then((posts) => {
          endList.current = posts.length === PAGE_SIZE;
          pageNumber.current = pageNumber.current + 1;
          currentPage.current = null;
          setLoading(false);
          const { getCommentsBatch } = useCommentStore.getState();
          getCommentsBatch({
            medium: CHANNEL_CONST.CHANNEL,
            mediumId: mediumId,
            postIds: posts,
            teamId: teamId
          });
        })
        .catch((err) => {
          console.info("get more post error", err);
        });
    }
  }

  function updatePinnedPost(postId, isPinned) {
    let methodName = "removePinnedPost";
    if (isPinned) {
      methodName = "addPinnedPost";
    }
    usePostStore.getState()[methodName](mediumId, postId);
    updateChild({
      type: isPinned
        ? CHANNEL_EVENTS.CHILD_POST_PINNED
        : CHANNEL_EVENTS.CHILD_POST_UNPINNED,
      childId: mediumId,
      postId
    });
  }

  function isFromNotification(postId) {
    return postNotification?.notificationData?.includes(postId);
  }

  return (
    <React.Fragment>
      {(newPostIds[mediumId] && newPostIds[mediumId].length) ||
      (newPosts[mediumId] && newPosts[mediumId].length) ? (
        <div className={styles.newPostsContainer}>
          <div className={styles.newPostsIndicator} onClick={syncNewPosts}>
            <Icon paddingNone={true}>
              <UpArrowIcon />
            </Icon>
            <Trans>New posts</Trans>
          </div>
        </div>
      ) : null}
      <div className={styles.container}>
        <InfiniteScrollList
          className={`${styles.postListWrapper} ${
            postList?.length > 0 && styles.wrapperAlign
          }`}
          thickness={SCROLLBAR_THICKNESS.MEDIUM}
          onEndReached={onEndReached}
          onScroll={(e) => {
            /**   logic to show up arrow icon */
            const isScrolledToUp = Math.trunc(e.scrollTop) > 100;
            setShowFloatingArrow(isScrolledToUp);
          }}
          isLoading={loading}
          loaderRenderer={() => {
            return (
              <PostSkeleton
                marginTop={true}
                loaderDelay={skeletonDelay.current ? 1000 : 0}
              />
            );
          }}
          ref={scrollRef}
          endOffset={50}
        >
          <div
            className={`${
              postList && postList.length === 0 && !loading
                ? styles.emptyDiscussionWrapper
                : styles.discussionWrapper
            } `}
            ref={postListRef}
          >
            <Postbox
              medium={CHANNEL_CONST.CHANNEL}
              mediumId={mediumId}
              createNew={enableEditor}
              teamId={teamId}
              isAnnouncement={mediumId === ANNOUNCEMENTS_CHANNEL_ID}
              onClose={() => {
                setEnableEditor(false);
              }}
            />
            <React.Fragment>
              {newPostLoading && <PostSkeleton marginTop={true} />}
              {postNotification?.notificationData && (
                <React.Fragment>
                  <div
                    className={`${styles.feedTitle} ${styles.notificationHeaderText}`}
                  >
                    {postNotification?.focusedText}
                  </div>
                  {postNotification?.notificationData?.map((notification) => {
                    return (
                      <Post
                        key={notification.EntityId}
                        postId={notification.EntityId}
                        roles={roles || []}
                        medium={CHANNEL_CONST.CHANNEL}
                        mediumId={mediumId}
                        postNotification={postNotification}
                        teamId={teamId}
                        postIndex={CHANNEL_CONST.POST + notification.EntityId}
                        shouldCallDefaultComments={false}
                      />
                    );
                  })}
                </React.Fragment>
              )}
              {pinnedPostIds &&
                pinnedPostIds.length - notifcationPinned.current > 0 && (
                  <React.Fragment>
                    <div className={`${styles.feedTitle} smallplus`}>
                      <Trans>Pinned</Trans>
                    </div>
                    {getPinnedData ? (
                      <div className={styles.postOuterContainer}>
                        {pinnedPostIds.map((pinnedPostId) => {
                          if (isFromNotification(pinnedPostId)) return null;
                          return (
                            <Post
                              postId={pinnedPostId}
                              key={pinnedPostId}
                              roles={roles || []}
                              medium={CHANNEL_CONST.CHANNEL}
                              teamId={teamId}
                              mediumId={mediumId}
                              updatePinnedPost={updatePinnedPost}
                              isAnnouncement={
                                mediumId === ANNOUNCEMENTS_CHANNEL_ID
                              }
                              shouldCallDefaultComments={false}
                              postIndex={CHANNEL_CONST.POST + pinnedPostId}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <PostSkeleton />
                    )}
                  </React.Fragment>
                )}
              {postList && postList.length > 0 && (
                <React.Fragment>
                  <div className={`${styles.feedTitle} h6`}>
                    <Trans>Feed</Trans>
                  </div>
                  <div className={styles.postOuterContainer}>
                    {postList.map((postId, index) => {
                      let value = index + 1;
                      return (
                        nonRenderPosts?.indexOf(postId) < 0 && (
                          <Post
                            key={postId}
                            postId={postId}
                            teamId={teamId}
                            roles={roles || []}
                            medium={CHANNEL_CONST.CHANNEL}
                            mediumId={mediumId}
                            updatePinnedPost={updatePinnedPost}
                            pinCount={pinnedPostIds ? pinnedPostIds.length : 0}
                            isAnnouncement={
                              mediumId === ANNOUNCEMENTS_CHANNEL_ID
                            }
                            postIndex={CHANNEL_CONST.POST + postId + value}
                            shouldCallDefaultComments={false}
                          />
                        )
                      );
                    })}
                  </div>
                </React.Fragment>
              )}
            </React.Fragment>
            {postList &&
              postList.length === 0 &&
              !loading &&
              pinnedPostIds.length === 0 && (
                <div className={styles.emptyStateWrapper}>
                  <div className={styles.emptyStateContainer}>
                    <EmptyState
                      img={<EmptyFeedIcon />}
                      text={i18n._(t`New posts will appear here`)}
                      size={EMPTY_STATE_SIZES.LARGE}
                      actionText={i18n._(t`Create a post`)}
                      onActionClick={() => setEnableEditor(true)}
                    />
                  </div>
                </div>
              )}
          </div>
        </InfiniteScrollList>
      </div>
      {showFloatingArrow && (
        <FloatingButton
          scrollRef={scrollRef}
          hideFloating={() => setShowFloatingArrow(false)}
        />
      )}
    </React.Fragment>
  );
}
